// PO 2026-02 · Contractor SMS Onboarding Foundation — paid-only.
//
// Trial / unpaid contractors see the banner ONLY when JobClose wants to
// keep them aware that Business Texting is included with the paid
// subscription. During the free trial the banner is informational —
// there is no "Finish setup" CTA that opens an actionable form,
// because SMS entitlement is exclusively paid.
//
// Paid contractors with incomplete compliance attestations see an
// actionable banner that routes them into the SMS section of the
// canonical setup surface. There is one setup owner; this banner is
// a Dashboard signpost, never a competing form.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageSquare, ArrowRight, X, Info } from "lucide-react";

const DISMISS_KEY = "jc_bt_prompt_dismissed_at";
const DISMISS_HOURS = 24;

export default function BusinessTextingPrompt() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (t && Date.now() - t < DISMISS_HOURS * 3600 * 1000) {
      setDismissed(true);
      return;
    }
    api
      .get("/orgs/me/sms-onboarding")
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null));
  }, []);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  if (dismissed || !status) return null;

  // Hide entirely once SMS is live — nothing to signpost.
  if (status.state === "live") return null;

  const isTrial = !status.is_paid;

  // Trial contractors get an informational card only. No actionable
  // setup CTA — Business Texting is included with paid JobClose.
  if (isTrial) {
    return (
      <div
        data-testid="business-texting-prompt"
        data-state="unpaid"
        className="jc-card border-l-4 border-[var(--jc-text-soft)] p-4 flex items-center gap-3 mb-4"
      >
        <div className="shrink-0 w-9 h-9 rounded-full bg-[var(--jc-text-soft)]/10 text-[var(--jc-text-soft)] flex items-center justify-center">
          <Info size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Business Texting</div>
          <p
            className="text-xs text-[var(--jc-text-soft)] mt-0.5"
            data-testid="business-texting-prompt-trial-copy"
          >
            Included with your paid JobClose subscription. No setup
            action is required during the free trial.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="business-texting-prompt-dismiss"
          aria-label="Dismiss for 24 hours"
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded hover:bg-[var(--jc-bg)] text-[var(--jc-text-soft)]"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Paid contractor. Route to the canonical management surface which
  // reads/writes the same canonical fields.
  const missingCount =
    (status.missing_compliance_fields || []).length
    + (status.missing_identity_fields || []).length;

  const stateLabelMap = {
    info_needed: "Finish setting up Business Texting",
    ready: "Business Texting — ready to activate",
    provisioning: "Business Texting — provisioning your number",
    verification_in_progress: "Business Texting — verification in progress",
    rejected: "Business Texting — verification needs attention",
  };
  const title = stateLabelMap[status.state] || "Business Texting setup";
  const cta = status.state === "info_needed" || status.state === "rejected"
    ? "Finish setup"
    : "View status";

  return (
    <div
      data-testid="business-texting-prompt"
      data-state={status.state}
      className="jc-card border-l-4 border-[var(--jc-blue)] p-4 flex items-center gap-3 mb-4"
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
        <MessageSquare size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{title}</div>
        <p className="text-xs text-[var(--jc-text-soft)] mt-0.5">
          {status.state === "info_needed"
            ? `You're set to use JobClose. To start texting customers, finish your Business Texting setup — ${missingCount || 1} step${(missingCount || 1) === 1 ? "" : "s"} left.`
            : "Business Texting is part of your paid JobClose subscription. Check the current status."}
        </p>
      </div>
      <Link
        to="/app/settings/business-texting"
        data-testid="business-texting-prompt-cta"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold jc-btn-blue shrink-0"
      >
        {cta} <ArrowRight size={12} />
      </Link>
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="business-texting-prompt-dismiss"
        aria-label="Dismiss for 24 hours"
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded hover:bg-[var(--jc-bg)] text-[var(--jc-text-soft)]"
      >
        <X size={14} />
      </button>
    </div>
  );
}
